import { useState } from 'react';
import { useQuery } from '../hooks/useQuery';
import { api, type PlanVersion } from '../lib/api';
import { Card, CardHeader, Select, Spinner, EmptyState, Badge } from '../components/ui';
import { PlanKpis, DepartmentBreakdown } from '../components/FpaSummary';
import { PlanPnlCharts, VarianceTrendChart } from '../components/FpaCharts';
import { fmtPct } from '../lib/fmt';

const STATUS_VARIANT: Record<string, 'gray' | 'amber' | 'blue' | 'green'> = {
  draft: 'gray', submitted: 'amber', approved: 'blue', locked: 'green', superseded: 'gray', restated: 'gray',
};

/** Exec FP&A overview: headline KPIs, P&L + cash trend, department mix, variance
 *  and forecast quality for a chosen plan — composed from existing endpoints. */
export default function AdminFpaDashboardPage() {
  const plansQ = useQuery(() => api.fpa.plans.list());
  const plans = plansQ.data?.plans ?? [];
  const [planId, setPlanId] = useState('');
  const effectiveId = planId || plans.find((p) => p.kind === 'budget')?.id || plans[0]?.id || '';
  const plan = plans.find((p) => p.id === effectiveId);

  return (
    <>
      <Card>
        <CardHeader>
          <div style={{ fontSize: 13, fontWeight: 500 }}>FP&amp;A dashboard</div>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>financial overview for a plan</span>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8, alignItems: 'center' }}>
            {plan && <Badge variant={STATUS_VARIANT[plan.status] ?? 'gray'}>{plan.status}</Badge>}
            <Select value={effectiveId} onChange={setPlanId}>
              {plans.length === 0 && <option value="">— no plans —</option>}
              {plans.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.kind})</option>)}
            </Select>
          </span>
        </CardHeader>
      </Card>

      {plansQ.isLoading ? <Spinner />
        : !effectiveId ? <EmptyState message="No plans yet — create one under FP&A → Plans." />
        : <DashboardBody key={effectiveId} planId={effectiveId} plan={plan} />}
    </>
  );
}

function DashboardBody({ planId, plan }: { planId: string; plan?: PlanVersion }) {
  const linesQ = useQuery(() => api.fpa.plans.lines(planId), [planId]);
  const varQ = useQuery(() => api.fpa.variance({}), []);
  const qualityQ = useQuery(() => api.fpa.planQuality(planId), [planId]);
  const lines = linesQ.data?.lines ?? [];
  const primary = varQ.data?.baselines?.[0];
  const quality = qualityQ.data;

  if (linesQ.isLoading) return <Spinner />;
  if (lines.length === 0) {
    return <EmptyState message={`No computed lines for ${plan?.name ?? 'this plan'} — recompute it on the P&L tab.`} />;
  }

  return (
    <>
      <div style={{ marginBottom: 8 }}><PlanKpis lines={lines} /></div>

      <Card>
        <CardHeader><div style={{ fontSize: 13, fontWeight: 500 }}>Trend</div><span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{plan?.horizon_start} → {plan?.horizon_end}</span></CardHeader>
        <PlanPnlCharts lines={lines} />
      </Card>

      <DepartmentBreakdown lines={lines} />

      {quality && quality.overall.count > 0 && (
        <Card>
          <CardHeader><div style={{ fontSize: 13, fontWeight: 500 }}>Forecast quality</div><span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>over closed periods</span></CardHeader>
          <div style={{ padding: '10px 16px', fontSize: 12, display: 'flex', gap: 18 }}>
            <span>MAPE <strong>{fmtPct(quality.overall.mape)}</strong></span>
            <span>Bias <strong style={{ color: quality.overall.bias < 0 ? 'var(--color-red-text)' : 'var(--color-green-text)' }}>{fmtPct(quality.overall.bias)}</strong></span>
            <span style={{ color: 'var(--color-text-muted)' }}>{quality.overall.count} cells</span>
          </div>
        </Card>
      )}

      {varQ.data && varQ.data.periods.length > 0 && (
        <Card>
          <CardHeader>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Variance</div>
            <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>actuals vs {primary ? primary.name : 'baseline'}</span>
          </CardHeader>
          <VarianceTrendChart rows={varQ.data.rows} periods={varQ.data.periods} primaryId={primary?.id ?? null} />
        </Card>
      )}
    </>
  );
}
